import { deactivateDeal } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import { useDealsListStore } from "@/store/deals-list-store";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Bulk "mark not premium" for the pipeline selection docks (desktop + mobile).
 * There's no bulk endpoint, so it fans out single deactivate calls and reports
 * partial failures rather than aborting the batch.
 */
export function useBulkDeactivateDeals(workspaceId: string) {
  const { refreshDeals } = useDealsListStore();
  const queryClient = useQueryClient();
  const [isDeactivating, setIsDeactivating] = useState(false);

  const deactivate = async (dealIds: string[], onDone?: () => void) => {
    if (dealIds.length === 0) return;
    setIsDeactivating(true);
    try {
      const results = await Promise.allSettled(
        dealIds.map((id) => deactivateDeal(workspaceId, id))
      );
      const rejected = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const ok = results.length - rejected.length;

      await refreshDeals(workspaceId);
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });

      if (rejected.length > 0) {
        console.error(
          "Bulk deactivate failed:",
          rejected.map((r) => r.reason)
        );
      }

      if (ok === 0) {
        toast.error(
          getErrorMessage(rejected[0]?.reason, "Failed to update deals")
        );
      } else if (rejected.length > 0) {
        toast.error(`Marked ${ok} as not premium, ${rejected.length} failed`);
      } else {
        toast.success(`Marked ${ok} deal${ok === 1 ? "" : "s"} as not premium`);
      }

      // Keep the selection on a total failure so the user can retry.
      if (ok > 0) onDone?.();
    } finally {
      setIsDeactivating(false);
    }
  };

  return { deactivate, isDeactivating };
}
